/**
 * errosDoApp — o que acontece no aparelho da pessoa, gravado em ANALYTICS_EVENTS.
 *
 * O servidor já registra quando ELE falha. O que faltava era o outro lado: o
 * backend devolveu 200 e, mesmo assim, a tela quebrou — um erro de JavaScript,
 * uma promise rejeitada sem ninguém ouvindo, um componente que lançou no
 * render. `logErrorOccurred` existia para isso e nunca foi chamado; o painel
 * (App › Crashes) nasceu em empty state por causa disso.
 *
 * DIFERENTE DO ANEL DO RELATO DE BUG (features/relatoBug/buffers.js): aquele
 * guarda tudo em memória, só para quem tem claim, e só sai quando a pessoa
 * relata. Este roda para a base inteira e ESCREVE — por isso é avarento:
 *  - repetição não conta (mesmo tipo + mesma mensagem = um evento por sessão);
 *  - teto por sessão (regras.js);
 *  - mensagem cortada e com dígitos longos mascarados (CPF, telefone);
 *  - `console.error` NÃO é ouvido: é ruidoso e quase nunca é o bug.
 *
 * E o que NÃO é erro nosso: uma falha de transporte com o aparelho offline é
 * a rede da pessoa. Isso alimenta o estado de conectividade (o modal) e vira,
 * no máximo, um evento de "episódio" — a prova, no painel, de que a falha
 * daquele horário foi de rede, não do app.
 */

import { logErrorOccurred } from '../services/analyticsService';
import { telaCorrente } from '../design/shell/telaCorrente';
import * as conectividade from './conectividade.js';
import {
  chaveDoErro,
  erroEhCancelamento,
  higienizar,
  topoDaPilha,
  urlContaComoRede,
  TETO_EPISODIOS_SESSAO,
  TETO_ERROS_SESSAO,
  TETO_REDE_SESSAO,
} from './regras.js';

const vistos = new Set();
let enviados = 0;
let enviadosRede = 0;
let episodios = 0;
let instalado = false;

const online = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);
const rota = () => (typeof window === 'undefined' ? null : window.location?.pathname || null);

function enviar(tipo, contexto, extra) {
  // Nunca deixar a telemetria derrubar o app que ela observa.
  try {
    return logErrorOccurred(tipo, telaCorrente(), contexto, {
      online: online(),
      rota: rota(),
      ...extra,
    });
  } catch {
    return undefined;
  }
}

/**
 * Um erro do app (JS, promise, render). Deduplicado e com teto.
 * @param {{ tipo: string, mensagem: string, stack?: string, origem?: string }} e
 */
export function reportarErro({ tipo, mensagem, stack, origem }) {
  const chave = chaveDoErro(tipo, mensagem);
  if (vistos.has(chave) || enviados >= TETO_ERROS_SESSAO) return;
  vistos.add(chave);
  enviados += 1;
  enviar(tipo || 'erro', higienizar(mensagem), {
    origem: origem ? higienizar(origem, 200) : null,
    pilha: topoDaPilha(stack),
  });
}

/**
 * Uma falha de TRANSPORTE (não chegou resposta). Sempre alimenta a
 * conectividade; só vira evento se o aparelho se dizia online — offline é a
 * rede da pessoa, e o episódio já registra isso uma vez.
 */
export function reportarFalhaDeRede({ url, detalhe }) {
  conectividade.registrarFalhaDeTransporte();
  if (!online()) return;
  const chave = chaveDoErro('rede', url);
  if (vistos.has(chave) || enviadosRede >= TETO_REDE_SESSAO) return;
  vistos.add(chave);
  enviadosRede += 1;
  enviar('rede', higienizar(`${url || '?'}: ${detalhe || 'sem resposta'}`), { url: url || null });
}

/** Um episódio de rede (caiu / ficou instável): um evento por episódio, com teto. */
function reportarEpisodio(tipo) {
  if (episodios >= TETO_EPISODIOS_SESSAO) return;
  episodios += 1;
  enviar(tipo, '', {});
}

/**
 * Liga tudo uma vez, antes do primeiro render. Idempotente.
 * Devolve a função que desfaz (usada em teste).
 */
export function instalar() {
  if (instalado || typeof window === 'undefined') return () => {};
  instalado = true;

  const aoErro = (evento) => {
    reportarErro({
      tipo: 'js',
      mensagem: evento?.message || 'erro sem mensagem',
      stack: evento?.error?.stack,
      origem: evento?.filename ? `${evento.filename}:${evento.lineno}:${evento.colno}` : null,
    });
  };

  const aoRejeitar = (evento) => {
    const r = evento?.reason;
    reportarErro({
      tipo: 'promise',
      mensagem: r?.message || (typeof r === 'string' ? r : 'rejeição sem mensagem'),
      stack: r?.stack,
      origem: r?.code || null,
    });
  };

  window.addEventListener('error', aoErro);
  window.addEventListener('unhandledrejection', aoRejeitar);

  // A sonda de rede: UM embrulho de fetch, que só OBSERVA. Resposta de
  // qualquer status = transporte de pé; exceção = transporte caído. O anel
  // do relato de bug, quando existe, embrulha por cima deste e restaura este
  // ao sair — os dois convivem.
  const fetchOriginal = window.fetch;
  if (typeof fetchOriginal === 'function') {
    window.fetch = async function telemetriaFetch(entrada, init) {
      const url = typeof entrada === 'string' ? entrada : entrada?.url;
      const conta = urlContaComoRede(url);
      try {
        const resposta = await fetchOriginal.call(this, entrada, init);
        if (conta) conectividade.registrarSucesso();
        return resposta;
      } catch (err) {
        if (conta && !erroEhCancelamento(err)) {
          reportarFalhaDeRede({ url: String(url).split('?')[0], detalhe: err?.message });
        }
        throw err;
      }
    };
  }

  const desligarConectividade = conectividade.instalar();
  let anterior = conectividade.lerEstado();
  const desassinar = conectividade.assinar((atual) => {
    if (anterior.online && !atual.online) reportarEpisodio('sem_conexao');
    if (!anterior.instavel && atual.instavel && atual.online) reportarEpisodio('conexao_instavel');
    anterior = atual;
  });

  return () => {
    window.removeEventListener('error', aoErro);
    window.removeEventListener('unhandledrejection', aoRejeitar);
    if (typeof fetchOriginal === 'function') window.fetch = fetchOriginal;
    desassinar();
    desligarConectividade();
    instalado = false;
  };
}

export default instalar;
